// src/page/ManageLicensePage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  ArrowLeft, CreditCard, ShieldAlert, PauseCircle, PlayCircle, 
  RefreshCw, Building, User, Calendar, Activity, AlertTriangle, Loader2 
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
// Shadcn UI Components
import { AppHeader } from '../components/ui/layout/app-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';

import { BASE_URL } from '../lib/api';

export default function ManageLicensePage() {
//   const { subscriptionId } = useParams();
const { userId } = useParams(); // 👈 subscriptionId ki jagah userId
  const navigate = useNavigate();
  const { toast } = useToast();

  // Core State
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]); // Available plans for upgrade/downgrade
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog States
  const [isChangePlanOpen, setIsChangePlanOpen] = useState(false);
  const [isPauseOpen, setIsPauseOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isResumeOpen, setIsResumeOpen] = useState(false);

  // Form States
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [resumeDate, setResumeDate] = useState('');
  const [cancelData, setCancelData] = useState({ immediate: false, reason: '' });

  // -------------------------------------------------------------
  // 1. DATA FETCHING
  // -------------------------------------------------------------
  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('superadmin_token');
      
      const response = await fetch(`${BASE_URL}/superadmin/subscriptions/user/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to fetch subscription');
      
      setSubscription(data.data);
      setSelectedPlanId(data.data.plan_id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePlans = async () => {
    // Ideally fetch from an API like GET /plans. Using mock data for UI demo if API isn't ready
    try {
      const token = localStorage.getItem('superadmin_token');
      const response = await fetch(`${BASE_URL}/plans`, { headers: { 'Authorization': `Bearer ${token}` }});
      if(response.ok) {
        const data = await response.json();
        setPlans(data.data || []);
      }
    } catch (e) {
      console.warn("Could not fetch plans, using fallback data");
    }
  };

  useEffect(() => {
    fetchSubscriptionDetails();
    fetchAvailablePlans();
  }, [userId]);

  // -------------------------------------------------------------
  // 2. ACTION HANDLERS (Connected to Backend APIs)
  // -------------------------------------------------------------
  const handleApiAction = async (endpoint, method, bodyData, successMsg, closeDialog) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('superadmin_token');
      
      const response = await fetch(`${BASE_URL}/superadmin/subscriptions/${subscription.id}${endpoint}`, {
        method,
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: bodyData ? JSON.stringify(bodyData) : undefined
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Action failed');

      // alert(successMsg); // Replace with Toast component if available
      // ✅ NAYA: Success Toast
      toast({
        title: "Success",
        description: successMsg,
      }); 

      // setSubscription(data.data); // Update UI with new state
      // 🟢 FIX: Smart State Merge (Industry Standard for Partial Updates)
      setSubscription(prevSub => ({
        ...prevSub,     // 1. Puraana saara data rakho
        ...data.data,   // 2. Naye updated fields (status, paused_at etc) se override karo
        
        // 3. Agar backend ne nested objects nahi bheje, toh puraane wale preserve karo
        user: data.data.user || prevSub.user,
        organization: data.data.organization || prevSub.organization,
        plan: data.data.plan || prevSub.plan
      }));
      closeDialog();
    } catch (err) {
      // alert(`Error: ${err.message}`);
      // ✅ NAYA: Destructive (Error) Toast
      toast({
        variant: "destructive",
        title: "Action Failed",
        description: err.message,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const submitChangePlan = () => {
   // ✅ NAYA: Warning Toast
    if(!selectedPlanId) {
      return toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please select a plan before updating.",
      });
    }
    handleApiAction('/change', 'PUT', { newPlanId: selectedPlanId }, 'Plan updated successfully!', () => setIsChangePlanOpen(false));
  };

  const submitPause = () => {
    const body = resumeDate ? { resume_at: new Date(resumeDate).toISOString() } : {};
    handleApiAction('/pause', 'POST', body, 'License paused successfully!', () => setIsPauseOpen(false));
  };

  // const submitResume = () => {
  //   if(window.confirm("Are you sure you want to resume this license?")) {
  //     handleApiAction('/resume', 'POST', null, 'License resumed successfully!', () => {});
  //   }
  // };

  // ✅ NAYA CODE
  const submitResume = () => {
    // Bina native prompt ke direct API call, aur close handler pass karo
    handleApiAction('/resume', 'POST', null, 'License resumed successfully!', () => setIsResumeOpen(false));
  };

  const submitCancel = () => {
    // ✅ NAYA: Warning Toast
    if(!cancelData.immediate && !cancelData.reason) {
      return toast({
        variant: "destructive",
        title: "Reason Required",
        description: "Please provide a reason for cancellation.",
      });
    }
    handleApiAction('/cancel', 'POST', cancelData, 'License cancelled successfully!', () => setIsCancelOpen(false));
  };

  // -------------------------------------------------------------
  // 3. UI HELPERS
  // -------------------------------------------------------------
  const getStatusBadge = (status) => {
    switch(status) {
      case 'ACTIVE': return <Badge className="bg-green-500 hover:bg-green-600">Active</Badge>;
      case 'PAUSED': return <Badge className="bg-amber-500 hover:bg-amber-600">Paused</Badge>;
      case 'CANCELLED': return <Badge variant="destructive">Cancelled</Badge>;
      case 'EXPIRED': return <Badge variant="secondary">Expired</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'dd MMM yyyy, hh:mm a');
  };

  if (loading) return <div className="p-10 flex justify-center items-center h-screen"><Loader2 className="animate-spin w-8 h-8 text-blue-600" /></div>;
  if (error) return <div className="p-10 text-center text-red-600"><ShieldAlert className="w-12 h-12 mx-auto mb-4"/><h2>{error}</h2><Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button></div>;
  if (!subscription) return null;

  const isCancelled = subscription.status === 'CANCELLED';

  return (
    <div className="min-h-screen bg-gray-50/50 pb-12">
      <AppHeader title="Manage License">
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Button>
      </AppHeader>

      <main className="max-w-5xl mx-auto p-6 space-y-6 mt-6">
        
        {/* TOP SECTION: User Identity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="shadow-sm border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2"><User className="w-5 h-5 text-blue-600"/> Account Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500 text-sm">Full Name</span>
                  <span className="font-medium">{subscription.user?.fullName || 'N/A'}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500 text-sm">Email</span>
                  <span className="font-medium">{subscription.user?.email || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Organization</span>
                  <span className="font-medium flex items-center gap-1">
                    <Building className="w-3 h-3 text-gray-400"/> {subscription.organization?.name || 'Individual'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* QUICK STATUS CARD */}
          <Card className="shadow-sm border-gray-200 bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center gap-2"><Activity className="w-5 h-5 text-blue-600"/> License Status</div>
                {getStatusBadge(subscription.status)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500 text-sm">Current Plan</span>
                  <span className="font-bold text-blue-700">{subscription.plan?.name || 'Free Tier'}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500 text-sm">Billing Cycle</span>
                  <span className="font-medium capitalize">{subscription.billing_cycle.toLowerCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Subscription ID</span>
                  <span className="font-mono text-xs bg-gray-100 p-1 rounded text-gray-600">{subscription.id}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* MIDDLE SECTION: Timestamps & Billing Info */}
        <Card className="shadow-sm border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Calendar className="w-5 h-5 text-purple-600"/> Billing & Timeline</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div>
                <Label className="text-gray-500 text-xs uppercase tracking-wider">Current Period Start</Label>
                <p className="font-medium mt-1">{formatDate(subscription.current_period_start)}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs uppercase tracking-wider">Current Period End</Label>
                <p className="font-medium mt-1">{formatDate(subscription.current_period_end)}</p>
              </div>
            </div>
            <div className="space-y-4">
              {subscription.paused_at && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Label className="text-amber-700 text-xs uppercase tracking-wider font-bold">Paused On</Label>
                  <p className="font-medium text-amber-900 mt-1">{formatDate(subscription.paused_at)}</p>
                  {subscription.resume_at && (
                    <p className="text-sm text-amber-700 mt-1">Scheduled to resume: {formatDate(subscription.resume_at)}</p>
                  )}
                </div>
              )}
              {subscription.cancelled_at && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <Label className="text-red-700 text-xs uppercase tracking-wider font-bold">Cancelled On</Label>
                  <p className="font-medium text-red-900 mt-1">{formatDate(subscription.cancelled_at)}</p>
                  <p className="text-sm text-red-700 mt-1 italic">Reason: {subscription.cancellation_reason}</p>
                </div>
              )}
              {subscription.cancel_at_period_end && !subscription.cancelled_at && (
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> This license is scheduled to cancel at the end of the billing period.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* BOTTOM SECTION: Superadmin Action Panel */}
        <Card className="border-blue-100 shadow-md">
          <CardHeader className="bg-blue-50/50 border-b border-blue-100">
            <CardTitle className="text-lg text-blue-900">Administrative Actions</CardTitle>
            <CardDescription>Use extreme caution. These actions immediately affect the user's live billing and access.</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
              {/* Action 1: Change Plan */}
              <Button 
                variant="outline" 
                className="h-24 flex flex-col gap-2 items-center justify-center border-gray-300 hover:border-blue-500 hover:bg-blue-50"
                onClick={() => setIsChangePlanOpen(true)}
                disabled={isCancelled}
              >
                <CreditCard className="w-6 h-6 text-blue-600" />
                <span className="font-semibold">Change / Upgrade Plan</span>
              </Button>

              {/* Action 2: Pause / Resume */}
              {subscription.status === 'PAUSED' ? (
                <Button 
                  variant="outline" 
                  className="h-24 flex flex-col gap-2 items-center justify-center border-green-200 bg-green-50 hover:bg-green-100 text-green-700"
                  // onClick={submitResume}
                  onClick={() => setIsResumeOpen(true)}
                  disabled={actionLoading}
                >
                  <PlayCircle className="w-6 h-6" />
                  <span className="font-semibold">Resume License</span>
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  className="h-24 flex flex-col gap-2 items-center justify-center border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700"
                  onClick={() => setIsPauseOpen(true)}
                  disabled={isCancelled}
                >
                  <PauseCircle className="w-6 h-6" />
                  <span className="font-semibold">Pause License</span>
                </Button>
              )}

              {/* Action 3: Cancel */}
              <Button 
                variant="outline" 
                className="h-24 flex flex-col gap-2 items-center justify-center border-red-200 bg-red-50 hover:bg-red-100 text-red-700"
                onClick={() => setIsCancelOpen(true)}
                disabled={isCancelled}
              >
                <ShieldAlert className="w-6 h-6" />
                <span className="font-semibold">Cancel License</span>
              </Button>

            </div>
          </CardContent>
        </Card>
      </main>

      {/* ================= MODALS SECTION ================= */}

      {/* 1. Change Plan Modal */}
      <Dialog open={isChangePlanOpen} onOpenChange={setIsChangePlanOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Subscription Plan</DialogTitle>
            <DialogDescription>Select a new plan. Prorations will be applied automatically via the payment gateway.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="mb-2 block">Available Plans</Label>
            <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a plan" />
              </SelectTrigger>
              <SelectContent>
                {/* Dynamically render plans from API. If empty, show fallback */}
                {plans.length > 0 ? (
                   plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.tier})</SelectItem>)
                ) : (
                   <SelectItem value={subscription.plan?.id || 'current'}>
                     {subscription.plan?.name || 'Current Plan'} (Current)
                   </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsChangePlanOpen(false)}>Close</Button>
            <Button onClick={submitChangePlan} disabled={actionLoading || selectedPlanId === subscription.plan_id}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null} Update Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2. Pause Modal */}
      <Dialog open={isPauseOpen} onOpenChange={setIsPauseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2"><PauseCircle className="w-5 h-5"/> Pause License</DialogTitle>
            <DialogDescription>
              Pausing will stop future billing but retain the user's data. You can optionally set an auto-resume date.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="resumeDate">Auto-Resume Date (Optional)</Label>
              <Input 
                id="resumeDate" 
                type="datetime-local" 
                value={resumeDate}
                onChange={(e) => setResumeDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)} // Cannot select past dates
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank to pause indefinitely until manually resumed.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPauseOpen(false)}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={submitPause} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null} Confirm Pause
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 3. Cancel Modal */}
      <Dialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
        <DialogContent className="border-red-200">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2"><ShieldAlert className="w-5 h-5"/> Cancel License</DialogTitle>
            <DialogDescription>
              This is a destructive action. The user will lose access to premium features.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-5">
            <div className="flex items-start space-x-3 p-3 bg-red-50 border border-red-100 rounded-md">
              <Checkbox 
                id="immediate" 
                checked={cancelData.immediate}
                onCheckedChange={(checked) => setCancelData({...cancelData, immediate: checked})}
              />
              <div className="grid gap-1.5 leading-none">
                <Label htmlFor="immediate" className="font-semibold text-red-900 cursor-pointer">Cancel Immediately</Label>
                <p className="text-sm text-red-700">If unchecked, the license will remain active until the end of the current billing cycle ({formatDate(subscription.current_period_end)}).</p>
              </div>
            </div>

            <div>
              <Label htmlFor="reason">Reason for Cancellation <span className="text-red-500">*</span></Label>
              <Textarea 
                id="reason" 
                placeholder="e.g., User requested via support ticket #1234" 
                className="mt-1"
                value={cancelData.reason}
                onChange={(e) => setCancelData({...cancelData, reason: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCancelOpen(false)}>Close</Button>
            <Button variant="destructive" onClick={submitCancel} disabled={actionLoading || !cancelData.reason.trim()}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null} Confirm Cancellation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 4. Resume Modal */}
      <Dialog open={isResumeOpen} onOpenChange={setIsResumeOpen}>
        <DialogContent className="border-green-200">
          <DialogHeader>
            <DialogTitle className="text-green-600 flex items-center gap-2">
              <PlayCircle className="w-5 h-5"/> Resume License
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to resume this license? The user will immediately regain access to their premium features, and their normal billing cycle will continue.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsResumeOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={submitResume} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null} 
              Confirm Resume
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}